import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useQuoteBuilderNav } from '../../contexts/QuoteBuilderNavContext';
import theme from '../../styles/travelieTheme';

/**
 * TravelieLayout - single global sidebar shell for every agent-facing page.
 *
 * Reskinned 2026-08-03 to the Voyager Pro palette (see AGENTS.md /
 * memory MCP "TPCRM MCP Server Config Fix" era session for the Figma
 * source). Previously only wrapped /agent/dashboard, with every other
 * agent route wrapped by the separate, top-nav-based AgentLayout.jsx -
 * that split caused a jarring layout change when navigating away from the
 * dashboard. AgentLayout is now retired (left in the repo, unused, in case
 * anything still imports it) and this component wraps ALL /agent/* routes,
 * with the full navigation menu (including the "Tools" group and
 * role/subscription-based filtering) ported over from it.
 *
 * Dropped from the old AgentLayout when merging: the decorative
 * background-gradient picker (agent-selectable page background, driven by
 * AgentProfile.jsx + `agentBackgroundTheme` localStorage/user field) is no
 * longer rendered here since it conflicts with the new fixed cream
 * background - the setting in AgentProfile still exists but is now a no-op
 * here. Also dropped a stale one-time "email delivery issue resolved"
 * announcement banner (dated key `email-fix-banner-dismissed-may14`).
 */

const TravelieLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Populated only while QuoteBuilder.js is mounted (see
  // QuoteBuilderNavContext) - lets the shared sidebar/header swap to a
  // Figma-style step nav + progress bar instead of the normal CRM nav.
  const { navState: quoteNav } = useQuoteBuilderNav();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setIsMobileSidebarOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const isActive = (path) => location.pathname === path;
  const isMenuActive = (children) => children.some(child => isActive(child.path));

  const isCommissionOnlyAgent = () => user && user.role === 'agent' && user.status === 'commission_only';
  const isAdmin = user && (user.role === 'agency_admin' || user.role === 'super_admin');
  const isMainStreet = user && (user.role === 'super_admin' || user.agency_code === 'MST' || Number(user.agency_id || user.agencyId) === 1);
  const isIndependent = user && user.role === 'independent_agent';
  // Independent agents live under /independent/* instead of /agent/* - same
  // shared pages (Leads, Quotes, Itineraries, Payment Links/Reminders,
  // Workflow Stages, Groups, Commissions, Profile), different URL prefix.
  // Some agency-only routes (Tools group, Voyager AI, Workflow Analytics as
  // a standalone page, Payment Reports, Task Templates/Workflow Automation)
  // don't exist under /independent/* at all, so those items are omitted
  // rather than pointed at a 404 - see App.js's "/independent" route block
  // for the authoritative list of what independent agents actually have.
  const basePath = isIndependent ? '/independent' : '/agent';

  // Full navigation menu - ported from the retired AgentLayout.jsx so every
  // page that was reachable before is still reachable here.
  const menuItems = [
    { id: 'overview', label: 'Overview', path: `${basePath}/dashboard`, icon: '📊' },
    ...(isMainStreet && !isIndependent ? [{ id: 'resources', label: 'Agent Resources', path: '/agent/resources', icon: 'AR' }] : []),
    {
      id: 'client-management',
      label: 'Client Management',
      icon: '👥',
      children: [
        { id: 'clients', label: 'Clients', path: `${basePath}/clients` },
        { id: 'workflow', label: 'Workflow Stages', path: `${basePath}/workflow` },
        { id: 'communications', label: 'Communications', path: `${basePath}/communications` },
        ...(isIndependent ? [] : [{ id: 'workflow-analytics', label: 'Workflow Analytics', path: '/agent/workflow-analytics' }]),
      ],
    },
    {
      id: 'tasks-projects',
      label: 'Tasks & Projects',
      icon: '📋',
      children: [
        { id: 'tasks', label: 'Tasks', path: `${basePath}/tasks` },
        ...(isAdmin ? [{ id: 'task-templates', label: 'Task Templates', path: '/agent/task-templates' }] : []),
        ...(!isIndependent ? [{ id: 'workflow-automation', label: 'Workflow Automation', path: '/agent/workflow-automation' }] : []),
      ],
    },
    {
      id: 'business-ops',
      label: 'Business Operations',
      icon: '💼',
      children: [
        { id: 'leads', label: 'Leads', path: `${basePath}/leads` },
        { id: 'quotes', label: 'Quotes', path: `${basePath}/quotes` },
        { id: 'itineraries', label: 'Itineraries', path: `${basePath}/itineraries` },
        { id: 'commissions', label: 'Commissions', path: `${basePath}/commissions` },
        // Agency agents' standalone Payments list was retired 2026-08-19 -
        // redundant with Payment Reports' History tab (see App.js). Left in
        // place for independent agents, whose page is still separate.
        ...(isIndependent ? [{ id: 'payments', label: 'Payments', path: `${basePath}/payments` }] : []),
        { id: 'payment-reminders', label: 'Payment Reminders', path: `${basePath}/payment-reminders` },
        { id: 'payment-links', label: 'Payment Links', path: `${basePath}/payment-links` },
        { id: 'groups', label: 'Groups', path: `${basePath}/groups` },
        ...(isIndependent ? [] : [{ id: 'activeagent-ai', label: 'Voyager AI', path: '/agent/activeagent-ai' }]),
      ],
    },
    {
      id: 'analytics',
      label: 'Analytics & Reports',
      icon: '📈',
      children: [
        { id: 'analytics-dashboard', label: 'Analytics Dashboard', path: `${basePath}/analytics` },
        ...(isIndependent ? [] : [{ id: 'payment-reports', label: 'Payment Reports', path: '/agent/payment-reports' }]),
      ],
    },
    ...(isIndependent ? [] : [{
      id: 'tools',
      label: 'Tools',
      icon: '🔧',
      children: [
        { id: 'calendar', label: 'Calendar', path: '/agent/calendar' },
        { id: 'supplier-portals', label: 'Supplier Portals', path: '/agent/supplier-portals' },
        { id: 'tripshare', label: 'TripShare', path: '/agent/tripshare' },
        { id: 'crowd-calendar', label: 'Theme Park Crowds', path: '/agent/crowd-calendar' },
        { id: 'ai-tools', label: 'AI Tools', path: '/agent/ai-tools' },
        { id: 'tax-forms', label: 'Tax Forms', path: '/agent/tax-forms' },
        { id: 'support', label: 'Support', path: '/agent/support' },
      ],
    }]),
    ...(isIndependent ? [{ id: 'subscription', label: 'Subscription', path: '/independent/subscription', icon: '💳' }] : []),
    ...(isIndependent ? [{ id: 'support', label: 'Support', path: '/independent/support', icon: '❓' }] : []),
    { id: 'profile', label: 'My Profile', path: `${basePath}/profile`, icon: '⚙️' },
  ];

  // Commission-only agents only ever see Profile, matching the retired
  // AgentLayout's behavior.
  const visibleMenuItems = isCommissionOnlyAgent()
    ? [{ id: 'profile', label: 'My Profile', path: '/agent/profile', icon: '⚙️' }]
    : menuItems;

  // Derive the current page title for the top bar from the active item.
  const getPageTitle = () => {
    for (const item of menuItems) {
      if (item.path && isActive(item.path)) return item.label;
      if (item.children) {
        const child = item.children.find(c => isActive(c.path));
        if (child) return child.label;
      }
    }
    return 'Travel+ CRM';
  };

  const sidebarWidth = isSidebarCollapsed ? '80px' : theme.layout.sidebar.width;

  const sidebarStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: sidebarWidth,
    background: theme.colors.navy.main,
    display: 'flex',
    flexDirection: 'column',
    transition: `width ${theme.transitions.normal}`,
    zIndex: 1000,
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  const mobileSidebarStyle = {
    ...sidebarStyle,
    transform: isMobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: `transform ${theme.transitions.normal}`,
  };

  const mainContentStyle = {
    marginLeft: isMobile ? '0' : sidebarWidth,
    minHeight: '100vh',
    background: theme.colors.neutral.background,
    transition: `margin-left ${theme.transitions.normal}`,
    display: 'flex',
    flexDirection: 'column',
    // `main` is a flex item in the outer sidebar+main row (.voyager-app); without
    // an explicit flex-basis/min-width it defaults to min-width:auto and won't
    // shrink below the intrinsic (min-content) width of whatever a page renders
    // inside it - e.g. a non-wrapping horizontally-scrolling row of filter tabs.
    // That silently forces the whole layout wider than the viewport on mobile.
    flex: '1 1 auto',
    minWidth: 0,
  };

  const logoStyle = {
    padding: theme.spacing.xl,
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: isSidebarCollapsed ? 'center' : 'space-between',
    flexShrink: 0,
  };

  const menuItemStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    margin: `0 ${theme.spacing.sm}`,
    borderRadius: '12px',
    color: active ? '#FFFFFF' : 'rgba(255,255,255,0.65)',
    background: active ? 'rgba(255,255,255,0.1)' : 'transparent',
    cursor: 'pointer',
    textDecoration: 'none',
    fontSize: theme.typography.fontSize.sm,
    fontWeight: active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.regular,
    transition: `all ${theme.transitions.fast}`,
    gap: theme.spacing.md,
  });

  const submenuItemStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    marginLeft: theme.spacing.lg,
    marginRight: theme.spacing.sm,
    borderRadius: '10px',
    color: active ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
    background: active ? theme.colors.primary.main : 'transparent',
    cursor: 'pointer',
    textDecoration: 'none',
    fontSize: theme.typography.fontSize.sm,
    fontWeight: active ? theme.typography.fontWeight.medium : theme.typography.fontWeight.regular,
    transition: `all ${theme.transitions.fast}`,
  });

  const renderMenuItem = (item) => {
    if (item.children) {
      const isExpanded = expandedMenus[item.id];
      const hasActiveChild = isMenuActive(item.children);

      return (
        <div key={item.id} style={{ marginBottom: '2px' }}>
          <div
            style={menuItemStyle(hasActiveChild)}
            onClick={() => toggleMenu(item.id)}
            onMouseEnter={(e) => { if (!hasActiveChild) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            onMouseLeave={(e) => { if (!hasActiveChild) e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ fontSize: '16px', flexShrink: 0 }}>{item.icon}</span>
            {!isSidebarCollapsed && (
              <>
                <span style={{ flex: 1 }}>{item.label}</span>
                <span style={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: `transform ${theme.transitions.fast}`,
                  fontSize: '10px',
                  opacity: 0.6,
                }}>
                  ▼
                </span>
              </>
            )}
          </div>
          {isExpanded && !isSidebarCollapsed && (
            <div style={{ marginTop: '2px' }}>
              {item.children.map(child => (
                <Link
                  key={child.id}
                  to={child.path}
                  style={submenuItemStyle(isActive(child.path))}
                  onMouseEnter={(e) => { if (!isActive(child.path)) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                  onMouseLeave={(e) => { if (!isActive(child.path)) e.currentTarget.style.background = 'transparent'; }}
                >
                  {child.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path}
        style={{ ...menuItemStyle(isActive(item.path)), marginBottom: '2px' }}
        onMouseEnter={(e) => { if (!isActive(item.path)) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
        onMouseLeave={(e) => { if (!isActive(item.path)) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ fontSize: '16px', flexShrink: 0 }}>{item.icon}</span>
        {!isSidebarCollapsed && <span>{item.label}</span>}
      </Link>
    );
  };

  // Figma-style step nav + live quote summary, shown in place of the normal
  // menu only while QuoteBuilder.js is mounted (see QuoteBuilderNavContext).
  const renderQuoteStepNav = () => {
    const activeIndex = quoteNav.steps.findIndex(s => s.id === quoteNav.activeStep);
    return (
      <div style={{ padding: `0 ${theme.spacing.md}` }}>
        {!isSidebarCollapsed && (
          <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: `0 ${theme.spacing.sm}`, marginBottom: theme.spacing.sm }}>
            Building Quote
          </div>
        )}
        {quoteNav.steps.map((step, i) => {
          const isActiveStep = i === activeIndex;
          const isDone = i < activeIndex;
          // Jumping backward/to the current step is always fine; jumping
          // forward requires the current step to be complete first.
          const canJump = isDone || isActiveStep || quoteNav.isStepReady(quoteNav.activeStep);
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => canJump && quoteNav.goToStep(step.id)}
              disabled={!canJump}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
                padding: `${theme.spacing.sm} ${theme.spacing.sm}`, marginBottom: '2px', borderRadius: '10px',
                background: isActiveStep ? 'rgba(196, 135, 31, 0.15)' : 'transparent',
                border: 'none', cursor: canJump ? 'pointer' : 'not-allowed', textAlign: 'left',
                opacity: canJump ? 1 : 0.4,
              }}
            >
              <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: theme.typography.fontWeight.bold,
                background: isActiveStep ? theme.colors.primary.main : isDone ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)',
                color: isActiveStep || isDone ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
              }}>
                {isDone ? '✓' : step.number}
              </div>
              {!isSidebarCollapsed && (
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold, color: '#FFFFFF' }}>{step.title}</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{step.description}</div>
                </div>
              )}
            </button>
          );
        })}

        {!isSidebarCollapsed && (quoteNav.client || quoteNav.tripTitle || quoteNav.componentsCount > 0) && (
          <div style={{ marginTop: theme.spacing.lg, paddingTop: theme.spacing.md, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: `0 ${theme.spacing.sm}`, marginBottom: theme.spacing.sm }}>
              Quote Summary
            </div>
            {quoteNav.client && (
              <div style={{ padding: theme.spacing.sm, background: 'rgba(255,255,255,0.05)', borderRadius: '10px', marginBottom: theme.spacing.xs }}>
                <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold, color: '#FFFFFF' }}>
                  {quoteNav.client.firstName} {quoteNav.client.lastName}
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{quoteNav.client.email}</div>
              </div>
            )}
            {quoteNav.tripTitle && (
              <div style={{ padding: theme.spacing.sm, background: 'rgba(255,255,255,0.05)', borderRadius: '10px', marginBottom: theme.spacing.xs }}>
                <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold, color: '#FFFFFF' }}>{quoteNav.tripTitle}</div>
                {quoteNav.travelDates?.start && quoteNav.travelDates?.end && (
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{quoteNav.travelDates.start} → {quoteNav.travelDates.end}</div>
                )}
              </div>
            )}
            {quoteNav.componentsCount > 0 && (
              <div style={{ padding: theme.spacing.sm, background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold, color: '#FFFFFF' }}>
                  {quoteNav.componentsCount} component{quoteNav.componentsCount !== 1 ? 's' : ''}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const userInitials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`;
  const userRoleLabel = user?.role === 'agency_admin' ? 'Agency Admin'
    : user?.role === 'super_admin' ? 'Super Admin'
    : user?.role === 'independent_agent' ? 'Independent Agent'
    : 'Travel Agent';

  const renderSidebarContent = (isMobileVariant) => (
    <>
      <div style={logoStyle}>
        {!isSidebarCollapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, minWidth: 0 }}>
            <img
              src="/Travel+ CRM Logo.png"
              alt="Travel+ CRM"
              style={{ height: '32px', width: '32px', objectFit: 'contain', borderRadius: '8px', flexShrink: 0 }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#FFFFFF', fontWeight: theme.typography.fontWeight.semibold, fontSize: theme.typography.fontSize.sm, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Travel+ CRM
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: theme.typography.fontSize.xs }}>{isIndependent ? 'Independent Agent' : 'Agent Dashboard'}</div>
            </div>
          </div>
        )}
        <button
          onClick={() => isMobileVariant ? setIsMobileSidebarOpen(false) : setIsSidebarCollapsed(!isSidebarCollapsed)}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: isMobileVariant ? '22px' : '18px', color: 'rgba(255,255,255,0.5)', padding: theme.spacing.sm, flexShrink: 0 }}
        >
          {isMobileVariant ? '✕' : (isSidebarCollapsed ? '→' : '←')}
        </button>
      </div>

      <nav style={{ flex: 1, paddingTop: theme.spacing.lg, overflowY: 'auto' }}>
        {quoteNav ? renderQuoteStepNav() : visibleMenuItems.map(item => renderMenuItem(item))}
      </nav>

      <div style={{ padding: theme.spacing.lg, borderTop: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
        {!isSidebarCollapsed && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.md }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF',
                fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.xs, flexShrink: 0,
              }}>
                {userInitials || 'A'}
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.first_name} {user?.last_name}
                </p>
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.xs, color: 'rgba(255,255,255,0.4)' }}>
                  {userRoleLabel}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              style={{
                width: '100%', padding: `${theme.spacing.sm} ${theme.spacing.md}`, background: 'rgba(255,255,255,0.06)',
                color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px',
                fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, cursor: 'pointer',
                transition: `all ${theme.transitions.fast}`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = theme.colors.status.error; e.currentTarget.style.color = '#FFFFFF'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
            >
              Logout
            </button>
          </>
        )}
      </div>
    </>
  );

  return (
    <div className="voyager-app" style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile overlay backdrop */}
      {isMobileSidebarOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)', zIndex: 999 }}
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar - desktop */}
      <aside style={{ ...sidebarStyle, display: isMobile ? 'none' : 'flex' }}>
        {renderSidebarContent(false)}
      </aside>

      {/* Sidebar - mobile */}
      <aside style={{ ...mobileSidebarStyle, display: isMobile ? 'flex' : 'none' }}>
        {renderSidebarContent(true)}
      </aside>

      {/* Main content */}
      <main style={mainContentStyle}>
        {/* Top bar - all breakpoints */}
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.md,
          padding: `${theme.spacing.lg} ${theme.spacing.xl}`, borderBottom: `1px solid ${theme.colors.neutral.border}`,
          background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(8px)', flexShrink: 0, position: 'sticky', top: 0, zIndex: 100,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, minWidth: 0 }}>
            {isMobile && (
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                style={{
                  flexShrink: 0, padding: theme.spacing.sm, borderRadius: '10px', border: `1px solid ${theme.colors.neutral.border}`,
                  background: 'transparent', cursor: 'pointer', color: theme.colors.text.primary, fontSize: '16px', lineHeight: 1,
                }}
              >
                ☰
              </button>
            )}
            <div style={{ minWidth: 0 }}>
              {quoteNav ? (
                <>
                  <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Step {quoteNav.steps.findIndex(s => s.id === quoteNav.activeStep) + 1} of {quoteNav.steps.length}
                  </div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {quoteNav.steps.find(s => s.id === quoteNav.activeStep)?.title}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Travel+ CRM
                  </div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {getPageTitle()}
                  </div>
                </>
              )}
            </div>
          </div>
          {quoteNav && (
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flexShrink: 0 }}>
              <div style={{ width: 96, height: 6, borderRadius: 999, background: theme.colors.neutral.border, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 999, background: theme.colors.primary.main,
                  width: `${Math.round(((quoteNav.steps.findIndex(s => s.id === quoteNav.activeStep) + 1) / quoteNav.steps.length) * 100)}%`,
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <span style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.secondary }} className="hidden-mobile">
                {Math.round(((quoteNav.steps.findIndex(s => s.id === quoteNav.activeStep) + 1) / quoteNav.steps.length) * 100)}%
              </span>
            </div>
          )}
        </header>

        {/* Page content */}
        <div style={{ flex: 1, padding: isMobile ? theme.spacing.lg : theme.spacing['2xl'] }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default TravelieLayout;
